import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateDisplay } from '@/components/date-display';
import { Separator } from '@/components/ui/separator';
import { OrderDetail } from '@/lib/api/orders';
import { formatMoney } from "@/lib/money";
import { StoreResourceBadge } from '@/app/stores/components/StoreResourceBadge';
import { toIncludedTaxDisplay } from '@/app/orders/[id]/utils/included-tax';

interface OrderSummaryCardProps {
  order: OrderDetail;
}

export function OrderSummaryCard({ order }: OrderSummaryCardProps) {
  const { t } = useTranslation('orders');
  const summary = order.summaries[0] ? toIncludedTaxDisplay(order.summaries[0]) : undefined;
  const totalAmount = summary?.totalAdjustedAmount || 0;
  const shippingCost = summary?.shippingCost || 0;
  const shippingMethod = order.checkoutSession?.shippingRate?.shippingRateName;
  // Tax added on top only: tax included in the prices is listed under the total.
  const totalTaxAmount = summary?.totalTaxAmount || 0;
  const includedTaxes = summary?.includedTaxes ?? [];
  const subtotalBeforeTax = Math.max(totalAmount - totalTaxAmount - shippingCost, 0);
  // A 0.00 Tax row next to "Includes X VAT" reads as a contradiction: dropped only in that case.
  const showTaxRow = totalTaxAmount !== 0 || includedTaxes.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('orderSummary')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-muted-foreground text-sm">{t('totalAmount')}</p>
            <p className="text-2xl font-bold">{formatMoney(totalAmount, order.currency)}</p>
            <p className="text-muted-foreground mt-1 text-xs">
              {order.items.length} {order.items.length !== 1 ? t('items') : t('item')}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">{t('paidAmount')}</p>
            <p className="text-2xl font-bold">{formatMoney(order.paidAmount || 0, order.currency)}</p>
            {order.paidAt && (
              <p className="text-muted-foreground mt-1 text-xs"><DateDisplay value={order.paidAt} /></p>
            )}
          </div>
        </div>
        <Separator />
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('subtotal')}</span>
            <span className="font-medium tabular-nums text-right min-w-[8rem]">{formatMoney(subtotalBeforeTax, order.currency)}</span>
          </div>
          {showTaxRow && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('taxAmount')}</span>
              <span className="font-medium tabular-nums text-right min-w-[8rem]">{formatMoney(totalTaxAmount, order.currency)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {t('shippingCost')}
              {shippingMethod ? <span className="text-foreground"> · {shippingMethod}</span> : null}
            </span>
            <span className="font-medium tabular-nums text-right min-w-[8rem]">{formatMoney(shippingCost, order.currency)}</span>
          </div>
          <Separator />
          <div className="flex justify-between text-sm font-semibold">
            <span>{t('total')}</span>
            <span className="tabular-nums text-right min-w-[8rem]">{formatMoney(totalAmount, order.currency)}</span>
          </div>
          {includedTaxes.map((line) => (
            <p key={`${line.description}:${line.amount}`} className="text-muted-foreground text-right text-xs">
              {t('includedTax', { amount: formatMoney(line.amount, order.currency) })}
            </p>
          ))}
        </div>
        <Separator />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-muted-foreground text-sm">{t('store')}</p>
            <StoreResourceBadge
              storeId={order.storeId}
              storeName={order.store.name}
            />
          </div>
          <div>
            <p className="text-muted-foreground text-sm">{t('currency')}</p>
            <p className="font-medium">{order.currency}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
